import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { memo, useCallback, useEffect, useRef } from 'react'
import {
    movePinnedRoom,
    openRoomState,
    pinRoom,
} from '../../../../../actions/room'
import { useHealthAppStateSelector } from '../../../../Hooks/useHealthAppState'
import { useUserSelector } from '../../../../Hooks/useUser'
import { palette } from '../../../../styles/colors'
import layers from '../../../../styles/layers'
import PinnedPortal from './PinnedPortal'

const RoomRoot = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: palette.pinnedRoomBackground,
    borderRadius: 6,
    margin: 4,
    marginBottom: 8,
    overflow: 'hidden',
    boxShadow: layers.one,
    transformStyle: 'preserve-3d',
    transition: 'transform 0.3s var(--spring-ease)',
    transform: 'scale(1)',
    '@starting-style': {
        transform: 'scale(0.95)',
    },
})

const mobileStyles = css({
    margin: 0,
})
const singlePinStyles = css({
    margin: 0,
    border: 'none',
    borderRadius: 0,
})

interface PinnedRoomsProps {
    roomId: number
    index: number
    // height: number
    width: number
    isMobile: boolean
    showChat: boolean
    singlePin: boolean
    darkMode: boolean
    vertical: boolean
}

const PinnedRoom = memo(
    ({
        roomId,
        index,
        // height,
        width,
        isMobile,
        showChat,
        singlePin,
        darkMode,
        vertical,
    }: PinnedRoomsProps) => {
        const mounted = useRef(false)
        const socketWasDisconnected = useRef(false)
        const socketConnected = useHealthAppStateSelector('connected')
        const id = useUserSelector(roomId, 'id')
        const slug = useUserSelector(roomId, 'slug')
        const banned = useUserSelector(roomId, 'banned')
        const isKicked = useUserSelector(roomId, 'isKicked')

        const loaded = !!roomId && socketConnected

        useEffect(() => {
            mounted.current = true
            return () => {
                mounted.current = false
                socketWasDisconnected.current = false
            }
        }, [])

        useEffect(() => {
            if (!mounted.current) return
            if (!socketConnected) {
                socketWasDisconnected.current = true
                return
            }
            if (socketWasDisconnected.current) {
                socketWasDisconnected.current = false
                if (banned) return
                openRoomState(slug, { forceSub: true, pinned: true })
            }
        }, [socketConnected])

        useEffect(() => {
            if (banned || isKicked) {
                pinRoom(id, { pinned: false })
            }
        }, [banned, isKicked])
        const dragStart = useCallback(
            (event) => {
                // event.preventDefault()
                event.dataTransfer.effectAllowed = 'move'
                event.dataTransfer.setData('text/plain', id)
            },
            [id],
        )
        const dropRoom = useCallback(
            (event) => {
                event.preventDefault()
                const idToMove = Number(
                    event.dataTransfer.getData('text/plain'),
                )
                movePinnedRoom(idToMove, index)
            },
            [index],
        )
        if (!id) {
            return null
        }

        return (
            <RoomRoot
                data-testid={`pinned-room-${id}`}
                id={`pinned-room-${id}`}
                css={
                    singlePin
                        ? singlePinStyles
                        : isMobile || vertical
                          ? mobileStyles
                          : null
                }
                style={{
                    width: vertical ? width : width - 8,
                }}
                draggable
                onDrop={dropRoom}
                onDragStart={dragStart}
            >
                <PinnedPortal
                    id={id}
                    loading={!loaded}
                    isMobile={isMobile}
                    // onSend={sendRoomMessage}
                    showChat={showChat}
                    darkMode={darkMode}
                    // height={height}
                    width={width}
                />
            </RoomRoot>
        )
    },
)

export default PinnedRoom
